import React from 'react';
import { Line } from 'react-chartjs-2';
import { RetirementProjection as RetirementProjectionType, PensionInputs, IndexInputs } from '../Interfaces';
import { formatCurrency } from '../Helpers/formatters';

interface RetirementProjectionProps {
  projection: RetirementProjectionType;
  pensionInputs: PensionInputs;
  indexInputs: IndexInputs;
  inflationRate: number;
}

export const RetirementProjection: React.FC<RetirementProjectionProps> = ({
  projection,
  pensionInputs,
  indexInputs,
  inflationRate,
}) => {
  const { pensionRetirementAge, indexRetirementAge } = projection;
  const pensionWithdrawalRate = pensionInputs.withdrawalRate;
  const indexWithdrawalRate = indexInputs.withdrawalRate;
  const startAge = Math.min(pensionRetirementAge, indexRetirementAge);
  const ages = Array.from({ length: 101 - startAge }, (_, i) => startAge + i);

  const pensionData = ages.map(age => {
    if (age < pensionRetirementAge) return null;
    return projection.pensionBalances[age - pensionRetirementAge] ?? null;
  });

  const indexData = ages.map(age => {
    if (age < indexRetirementAge) return null;
    return projection.indexBalances[age - indexRetirementAge] ?? null;
  });

  const pensionAnnualIncome = (projection.pensionBalances[0] ?? 0) * (pensionWithdrawalRate / 100);
  const indexAnnualIncome = (projection.indexBalances[0] ?? 0) * (indexWithdrawalRate / 100);

  const pensionYearsToRetirement = pensionInputs.endAge - pensionInputs.startAge;
  const indexYearsToRetirement = indexInputs.endAge - indexInputs.startAge;
  const deflate = (nominal: number, years: number) =>
    nominal / Math.pow(1 + inflationRate / 100, years);
  const pensionRealIncome = deflate(pensionAnnualIncome, pensionYearsToRetirement);
  const indexRealIncome = deflate(indexAnnualIncome, indexYearsToRetirement);

  // Use == null (not ===) to catch both null and undefined — WhenWritingNull on the API
  // omits null properties from JSON, so the frontend receives undefined, not null.
  const longevityLabel = (age: number | null | undefined) =>
    age == null ? 'Lasts beyond 100' : `Runs out at age ${age}`;

  const chartData = {
    labels: ages,
    datasets: [
      {
        label: `Pension (${pensionWithdrawalRate}% drawdown)`,
        data: pensionData,
        borderColor: '#667eea',
        backgroundColor: 'rgba(102, 126, 234, 0.08)',
        borderWidth: 3,
        fill: true,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
        spanGaps: false,
      },
      {
        label: `Index Fund (${indexWithdrawalRate}% drawdown)`,
        data: indexData,
        borderColor: '#764ba2',
        backgroundColor: 'rgba(118, 75, 162, 0.08)',
        borderWidth: 3,
        fill: true,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
        spanGaps: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    interaction: { intersect: false, mode: 'index' as const },
    plugins: {
      title: {
        display: true,
        text: 'Retirement Drawdown Projection',
        font: { size: 16, weight: 'bold' as const },
      },
      legend: {
        position: 'top' as const,
        labels: { usePointStyle: true, font: { size: 12 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const nominal = ctx.parsed.y;
            const age = Number(ctx.label);
            const yearsFromToday = age - Math.min(pensionInputs.startAge, indexInputs.startAge);
            const real = nominal / Math.pow(1 + inflationRate / 100, yearsFromToday);
            const realStr = inflationRate > 0 ? ` (${formatCurrency(real)} in today's money)` : '';
            return `${ctx.dataset.label}: ${formatCurrency(nominal)}${realStr}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Age', font: { size: 14, weight: 'bold' as const } },
        grid: { color: 'rgba(0,0,0,0.1)' },
      },
      y: {
        title: { display: true, text: 'Fund Value (£)', font: { size: 14, weight: 'bold' as const } },
        ticks: { callback: (v: any) => formatCurrency(v) },
        grid: { color: 'rgba(0,0,0,0.1)' },
        min: 0,
      },
    },
  };

  return (
    <div className="retirement-projection">
      <h2>🎯 Retirement Drawdown Projection</h2>
      <p className="projection-subtitle">
        Estimated fund value from retirement to age 100
      </p>

      <div className="projection-stats">
        <div className="projection-stat-card pension-stat">
          <div className="stat-label">💼 Pension ({pensionWithdrawalRate}% drawdown)</div>
          <div className="stat-income">{formatCurrency(pensionAnnualIncome)} / year</div>
          <div className="stat-monthly">{formatCurrency(pensionAnnualIncome / 12)} / month</div>
          <div className="stat-real-income">≈ {formatCurrency(pensionRealIncome)} / year in today's money</div>
          <div className={`stat-longevity ${projection.pensionRunsOutAge != null ? 'runs-out' : 'lasts'}`}>
            {longevityLabel(projection.pensionRunsOutAge)}
          </div>
        </div>

        <div className="projection-stat-card index-stat">
          <div className="stat-label">📈 Index Fund ({indexWithdrawalRate}% drawdown)</div>
          <div className="stat-income">{formatCurrency(indexAnnualIncome)} / year</div>
          <div className="stat-monthly">{formatCurrency(indexAnnualIncome / 12)} / month</div>
          <div className="stat-real-income">≈ {formatCurrency(indexRealIncome)} / year in today's money</div>
          <div className={`stat-longevity ${projection.indexRunsOutAge != null ? 'runs-out' : 'lasts'}`}>
            {longevityLabel(projection.indexRunsOutAge)}
          </div>
        </div>
      </div>

      <div className="chart-container">
        <Line data={chartData} options={chartOptions} />
      </div>

      <p className="projection-note">
        Chart shows nominal (future) values. Hover to see purchasing power in today's money at each age, accounting for {inflationRate}% annual inflation compounded from age {Math.min(pensionInputs.startAge, indexInputs.startAge)}. Assumes net annual growth of {(pensionInputs.annualReturn - 0.75).toFixed(2)}% (pension) and {(indexInputs.annualReturn - 0.2).toFixed(2)}% (index fund) after charges.
      </p>
    </div>
  );
};
